<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <v-form ref="resourcesForm" v-model="valid">
    <span class="font-18">
      {{ $t('workloadManagement.vmCurrentResources') }}
    </span>
    <v-col id="vmResourcesNumberOfVirtualCpuDiv" class="pl-0 pr-5 mt-5" lg="9">
      <v-text-field
        :id="`iiotVmResourcesCpuResource${id}`"
        v-model="cpu"
        :label="$t('workloadManagement.numberOfVirtualCpus')"
        :rules="rulesForCpu"
        :data-cy="`iiotVmResourcesCpuResource${id}`"
        :disabled="!editResources"
        class="number-class"
        type="number"
        required
        @input="changedNumberOfCpus"
      />
    </v-col>
    <v-row class="mt-3">
      <v-col lg="8" class="pl-0">
        <v-text-field
          :id="`iiotVmResourcesMemoryResource${id}`"
          v-model="memory"
          :label="$t('workloadManagement.limitMemory')"
          :rules="rulesForMemory"
          :data-cy="`iiotVmResourcesMemoryResource${id}`"
          :disabled="!editResources"
          class="input pa-0 ml-4 mt-0"
          required
          @input="memoryInput"
        />
      </v-col>
      <v-col
        :id="`iiotSelectMemoryMeasureVmResources${id}`"
        :data-cy="`iiotSelectMemoryMeasureVmResources${id}`"
        lg="4"
        class="pr-2"
      >
        <v-select
          v-model="selected"
          :items="array"
          :disabled="!editResources"
          data-cy="iiotResourcesAlloacationMemoryMeasureSelector"
          class="selector-class pa-0 ml-1 mt-0"
        />
      </v-col>
    </v-row>
    <div class="class-for-buttons">
      <v-row align="center">
        <v-col>
          <nerve-button
            v-if="editResources"
            :id="`iiotVmResourcesUpdateBtn${id}`"
            :text="$t('workloadManagement.updateBtn')"
            :data-cy="`iiotVmResourcesUpdateBtn${id}`"
            :disabled="rulesForUpdate"
            type-of-btn="action"
            size="normal"
            class="ma-0 mt-3 mb-4"
            @click-event="update()"
          />
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script>
import NerveButton from './NerveButton.vue';

export default {
  name: 'VmResources',
  components: {
    NerveButton,
  },
  props: {
    inputCpu: {
      type: Number,
      default: 0,
    },
    inputMemory: {
      type: Number,
      default: 0,
    },
    inputDeviceId: {
      type: Number,
      default: 0,
    },
    id: {
      type: String,
      default: '',
    },
    editResources: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    valid: false,
    array: ['KB', 'MB', 'GB', 'TB'],
    cpu: 0,
    deviceId: 0,
    memory: 0,
    memoryInBytes: 0,
    selected: 'MB',
    measureIndex: {
      B: 0,
      KB: 1,
      MB: 2,
      GB: 3,
      TB: 4,
    },
    memoryChanged: false,
    cpuChanged: false,
  }),
  computed: {
    rulesForCpu() {
      return [
        (number) => !!number || this.$t('workloadManagement.requiredCpu'),
        (number) => (number >= 1 && number <= 256) || this.$t('workloadManagement.cpuNumbersMessage'),
      ];
    },
    rulesForUpdate() {
      const rules = this.rulesOfMemoryInput(this.memory);
      const cpuRules = this.rulesCpu(this.cpu);
      const i = this.measureIndex[this.selected];
      const tempMemInBytes = Math.round((this.memory / 1000 ** (-1 * i)) * 100) / 100;
      if (tempMemInBytes === this.inputMemory && this.cpu === this.inputCpu) {
        return true;
      }
      if (rules === false) {
        return true;
      }
      if (cpuRules === false) {
        return true;
      }
      return false;
    },
    rulesForMemory() {
      if (this.selected === 'GB') {
        return [
          (input) => !!input || this.$t('workloadManagement.requiredMemory'),
          (input) => !Number.isNaN(Number(input)) || this.$t('workloadManagement.invalidMemoryMessage'),
          (input) => (input >= 0.1 && input <= 20) || this.$t('workloadManagement.memoryGBMinValueMessage'),
        ];
      }
      if (this.selected === 'KB') {
        return [
          (input) => !!input || this.$t('workloadManagement.requiredMemory'),
          (input) => !Number.isNaN(Number(input)) || this.$t('workloadManagement.invalidMemoryMessage'),
          (input) => (input >= 100000 && input <= 20000000) || this.$t('workloadManagement.memoryKBMinValueMessage'),
        ];
      }
      if (this.selected === 'MB') {
        return [
          (input) => !!input || this.$t('workloadManagement.requiredMemory'),
          (input) => !Number.isNaN(Number(input)) || this.$t('workloadManagement.invalidMemoryMessage'),
          (input) => (input >= 100 && input <= 20000) || this.$t('workloadManagement.memoryMBMinValueMessage'),
        ];
      }
      return [
        (input) => !!input || this.$t('workloadManagement.requiredMemory'),
        // eslint-disable-next-line no-restricted-globals
        (input) => !Number.isNaN(Number(input)) || this.$t('workloadManagement.invalidMemoryMessage'),
        (input) =>
          (input >= 0.00009999999999999999 && input <= 0.02) || this.$t('workloadManagement.memoryTBMinValueMessage'),
      ];
    },
  },
  watch: {
    selected() {
      this.$nextTick(() => {
        if (this.$refs.resourcesForm) {
          this.$refs.resourcesForm.validate();
        }
      });
    },
    inputCpu() {
      this.writingNewInputValues();
    },
    inputMemory() {
      this.writingNewInputValues();
    },
  },
  created() {
    this.cpu = this.inputCpu;
    this.memory = this.inputMemory;
    this.memoryInBytes = this.inputMemory;
    this.deviceId = this.inputDeviceId;
    this.initialCalcualtionOfMem();
  },
  methods: {
    update() {
      const data = {
        deviceId: this.deviceId,
      };
      if (this.memoryChanged) {
        data.memory = `${this.memory}${this.selected}`;
        this.memoryChanged = false;
      }
      if (this.cpuChanged) {
        data.cpu = this.cpu;
        this.cpuChanged = false;
      }
      this.$emit('save', data);
    },
    initialCalcualtionOfMem() {
      const sizes = Object.keys(this.measureIndex);
      if (this.memoryInBytes === 0) {
        this.memory = 0;
        // eslint-disable-next-line prefer-destructuring
        this.selected = sizes[0];
        return;
      }
      const i = parseInt(Math.floor(Math.log(this.memoryInBytes) / Math.log(1000)), 10);
      this.memory = Math.round((this.memoryInBytes / 1000 ** i) * 100) / 100;
      this.selected = sizes[i];
    },
    changedNumberOfCpus(numberOfCpus) {
      numberOfCpus = parseInt(numberOfCpus, 10);
      if (numberOfCpus === this.inputCpu) {
        this.cpu = numberOfCpus;
        this.cpuChanged = false;
        return;
      }
      this.cpu = numberOfCpus;
      this.cpuChanged = true;
    },
    memoryInput(memory) {
      if (memory === '') {
        return;
      }
      if (!Number.isNaN(Number(memory))) {
        memory = parseFloat(memory);
      }
      const i = this.measureIndex[this.selected];
      const temp = this.memoryInBytes / 1000 ** i;
      if (memory === temp) {
        this.memory = memory;
        this.memoryChanged = false;
        return;
      }
      this.memory = memory;
      this.memoryChanged = true;
    },
    rulesOfMemoryInput(memory) {
      switch (this.selected) {
        case 'MB':
          if (!Number.isNaN(Number(memory)) && memory !== '' && memory >= 100 && memory <= 20000) {
            return true;
          }
          return false;
        case 'KB':
          if (!Number.isNaN(Number(memory)) && memory !== '' && memory >= 100000 && memory <= 20000000) {
            return true;
          }
          return false;

        case 'TB':
          if (!Number.isNaN(Number(memory)) && memory !== '' && memory >= 0.00009999999999999999 && memory <= 0.02) {
            return true;
          }
          return false;
        case 'GB':
          if (!Number.isNaN(Number(memory)) && memory !== '' && memory >= 0.1 && memory <= 20) {
            return true;
          }
          return false;
        default:
          return true;
      }
    },
    rulesCpu(cpu) {
      // eslint-disable-next-line no-restricted-globals
      if (!Number.isNaN(Number(cpu)) && cpu !== '' && cpu >= 1 && cpu <= 256) {
        return true;
      }
      return false;
    },
    writingNewInputValues() {
      this.cpu = this.inputCpu;
      this.memory = this.inputMemory;
      this.memoryInBytes = this.inputMemory;
      this.deviceId = this.inputDeviceId;
      this.initialCalcualtionOfMem();
    },
  },
};
</script>
<style lang="scss">
.primary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.tertiary-tttech-background-for-btn {
  background-image: linear-gradient(to bottom, var(--v-tertiary-lighten4), var(--v-tertiary-base));
}

.btn-appearance {
  @extend .primary-tttech-background-for-btn;
  width: 100%;
  height: 40px !important;
  text-transform: none;
  color: var(--v-secondary-base) !important;
  font-weight: bold !important;
  font-size: 16px;
  line-height: 1px;
  flex: 1 0 auto;
}

.btns {
  @extend .btn-appearance;
  margin-top: 76px;
  margin-bottom: 40px;
  &.cancel {
    @extend .tertiary-tttech-background-for-btn;
  }
}
.btns[disabled] {
  background-image: none;
  background-color: #e9e9e9;
  color: #ffffff;
}

.input-appearance {
  max-width: 425px;
}

.input {
  @extend .input-appearance;
  margin-top: 76px;
  width: 100%;
}
.selector-class {
  width: 100%;
}
.number-class {
  padding-left: 4px;
  width: 100%;
}

.font-18 {
  font-size: 18px;
}

#vmResourcesNumberOfVirtualCpuDiv .v-text-field .v-label {
  overflow: unset !important;
  height: 10px;
}
</style>
